import React, { FormEvent, useEffect, useState } from 'react';
import { PrismBanner, PrismButton } from '@prism-ui/react';
import useFetch from '../../../../hooks/useFetch';
import DatePicker from '../../../DatePicker';
import axios from 'axios';
import Loader from '../../../Loader';
import ErrorMessage from '../../../ErrorMessage';
import { toast } from '../../../Toaster/toast';
import useSetTimeout from '../../../../hooks/useSetTimeout';
import './ApplicationWindow.css';
import AdminPane from '../../AdminPane';

// Define props interface
interface ApplicationWindowProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentUser?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
  loginAsUser?: (id: string) => void;
  endLoginAs?: () => void;
}

// Define the Chapter type
type Chapter = {
  id: number;
  location: string;
  distro: string;
};

// Define the ApplicationWindowData type
type ApplicationWindowData = {
  chapter_id: number;
  opening: string;
  soft_deadline: string;
  hard_deadline: string;
  notification_date: string;
  isNew?: boolean;
};

const ApplicationWindow: React.FC<ApplicationWindowProps> = ({
  currentUser,
}) => {
  const { setTimeout } = useSetTimeout();

  const {
    data: chapters,
    loading: chaptersLoading,
    error: chaptersError,
  } = useFetch<Chapter[]>('/api/chapters');
  const {
    data: applicationWindows,
    loading: windowsLoading,
    error: windowsError,
    refresh,
  } = useFetch<ApplicationWindowData[]>('/api/application_windows/all');

  const [windows, setWindows] = useState<ApplicationWindowData[]>([]);
  const [originalWindows, setOriginalWindows] = useState<ApplicationWindowData[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [sortAsc, setSortAsc] = useState(true);

  // Check if user has restricted access (Role.id == 7)
  const isRestrictedUser = currentUser?.Role?.id === 7;
  const userChapterId = currentUser?.chapter_id;

  // Function to check if user can edit a specific chapter
  const canEditChapter = (chapterId: number): boolean => {
    if (!isRestrictedUser) {
      // Unrestricted users can edit all chapters
      return true;
    }
    
    // Restricted users can only edit their own chapter
    return chapterId === userChapterId;
  };

  // Function to initialize/reset windows data
  const initializeWindows = () => {
    if (chapters && applicationWindows) {
      const windowsMap = new Map(
        applicationWindows.map((w) => [w.chapter_id, w])
      );

      // ALWAYS show all chapters regardless of user role
      const initialWindows = chapters.map((chapter) => {
        const existingWindow = windowsMap.get(chapter.id);
        if (existingWindow) {
          return existingWindow;
        }

        // Create default window for chapters without one
        const defaultDate = new Date().toISOString();
        return {
          chapter_id: chapter.id,
          opening: defaultDate,
          soft_deadline: defaultDate,
          hard_deadline: defaultDate,
          notification_date: defaultDate,
        };
      });

      setWindows(initialWindows);
      setOriginalWindows(JSON.parse(JSON.stringify(initialWindows))); // Deep copy
      setHasChanges(false);
    }
  };

  // Initialize windows data when chapters and application windows are loaded
  useEffect(() => {
    initializeWindows();
  }, [chapters, applicationWindows]);


  const updateWindow = (
    chapterId: number,
    field: keyof ApplicationWindowData,
    value: string | number
  ) => {
    // Check if user is authorized to edit this chapter
    if (!canEditChapter(chapterId)) {
      toast.error('You are not authorized to edit this chapter\'s application window.');
      return;
    }

    setWindows((prev) =>
      prev.map((window) =>
        window.chapter_id === chapterId ? { ...window, [field]: value } : window
      )
    );
    setHasChanges(true);
  };

  const validateWindows = () => {
    const errors: string[] = [];

    // Only validate chapters the user can edit
    windows.filter(window => canEditChapter(window.chapter_id)).forEach((window) => {
      const chapterName = getChapterName(window.chapter_id);
      const opening = new Date(window.opening);
      const softDeadline = new Date(window.soft_deadline);
      const hardDeadline = new Date(window.hard_deadline);
      const notification = new Date(window.notification_date);

      // Check if opening is before soft deadline
      if (opening >= softDeadline) {
        errors.push(
          `${chapterName}: Opening date must be before soft deadline`
        );
      }

      // Check if soft deadline is before hard deadline
      if (softDeadline > hardDeadline) {
        errors.push(
          `${chapterName}: Soft deadline must be before or equal to hard deadline`
        );
      }

      // Check if notification date is after hard deadline
      if (hardDeadline > notification) {
        errors.push(
          `${chapterName}: Notification date should be after hard deadline`
        );
      }
    });

    return errors;
  };

  // Also invoked directly by the Save button: prism-button lives in shadow DOM, so
  // its click never reaches the surrounding form's submit handler.
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();

    // Validate the data (only for editable chapters)
    const validationErrors = validateWindows();
    if (validationErrors.length > 0) {
      toast.error(`Validation errors: ${validationErrors.join('; ')}`);
      return;
    }

    setSubmitting(true);

    try {
      // Only send chapters the user is authorized to edit
      const windowsToSave = windows
        .filter(window => canEditChapter(window.chapter_id))
        .map(({ isNew, ...window }) => ({
          ...window,
          updated_by: currentUser?.id || currentUser?.username,
        }));

      if (windowsToSave.length === 0) {
        toast.error('No authorized changes to save.');
        setSubmitting(false);
        return;
      }

      await axios.put('/api/application_windows/all', {
        windows: windowsToSave,
        user_id: currentUser?.id,
        user_role_id: currentUser?.Role?.id,
        user_chapter_id: currentUser?.chapter_id,
      });
      
      await refresh();
      // Update original windows to clear change highlighting after successful save
      setOriginalWindows(JSON.parse(JSON.stringify(windows)));
      toast.success('Application windows have been updated successfully.');
      setHasChanges(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error updating application windows:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update application windows';
      toast.error(errorMessage);
    } finally {
      // Managed timer: a raw `window.setTimeout` here can fire after the pane unmounts.
      setTimeout(() => setSubmitting(false), 500);
    }
  };

  const getChapterName = (chapterId: number) => {
    const chapter = chapters?.find((c) => c.id === chapterId);
    if (chapter) {
      return chapter.location;
    }
    return 'Unknown Chapter';
  };

  const toggleSort = () => {
    setSortAsc(!sortAsc);
  };

  // Check if a specific field has been changed from its original value
  const hasFieldChanged = (chapterId: number, field: keyof ApplicationWindowData): boolean => {
    const currentWindow = windows.find(w => w.chapter_id === chapterId);
    const originalWindow = originalWindows.find(w => w.chapter_id === chapterId);
    
    if (!currentWindow || !originalWindow) return false;
    
    return currentWindow[field] !== originalWindow[field];
  };

  const getSortedWindows = () => {
    return [...windows].sort((a, b) => {
      const nameA = getChapterName(a.chapter_id).toLowerCase();
      const nameB = getChapterName(b.chapter_id).toLowerCase();
      const comparison = nameA.localeCompare(nameB);
      return sortAsc ? comparison : -comparison;
    });
  };

  // Check if user has any editable chapters
  const hasEditableChapters = windows.some(window => canEditChapter(window.chapter_id));

  if ((chaptersLoading && !chapters) || (windowsLoading && !applicationWindows)) {
    return <Loader />;
  }

  if (chaptersError || windowsError) {
    return (
      <ErrorMessage
        error={(chaptersError || windowsError)?.toString() || 'Unknown error'}
      />
    );
  }

  return (
    <AdminPane as="div" width="wide" className="admin application-window-container">
      <div className="application-window-header">
        <PrismBanner tone="info">
          <strong>Application Window Management:</strong>
          <ul className="application-window-help-list">
            <li><strong>Opening Date:</strong> Applications cannot be started before this date</li>
            <li><strong>Soft Deadline:</strong> This is the displayed deadline shown to applicants</li>
            <li><strong>Hard Deadline:</strong> Applications cannot be submitted after this date</li>
            <li><strong>Notification Date:</strong> The date by which the cohort will notify applicants of their status</li>
          </ul>
        </PrismBanner>
      </div>

      <form onSubmit={submit}>

      <table className="application-window-table">
        <thead>
          <tr>
            <th onClick={toggleSort} className="application-window-sort-header">
              Chapter {sortAsc ? '▲' : '▼'}
            </th>
            <th>
              Opening Date
              <br />
              <small className="application-window-col-hint">Applications open</small>
            </th>
            <th>
              Soft Deadline
              <br />
              <small className="application-window-col-hint">Displayed deadline</small>
            </th>
            <th>
              Hard Deadline
              <br />
              <small className="application-window-col-hint">Hard deadline</small>
            </th>
            <th>
              Notification Date
              <br />
              <small className="application-window-col-hint">Notify by date</small>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Display ALL chapters regardless of permissions */}
          {getSortedWindows().map((window) => {
            const canEdit = canEditChapter(window.chapter_id);
            
            return (
              <tr
                key={window.chapter_id}
                className={!canEdit ? 'application-window-row-readonly' : ''}
              >
                <td>
                  <strong>{getChapterName(window.chapter_id)}</strong>
                  {!canEdit && (
                    <small className="application-window-readonly-note">
                      <em>(Read-only - No edit permissions)</em>
                    </small>
                  )}
                </td>
                <td>
                  <DatePicker
                    utc
                    value={window.opening}
                    onChange={(date) =>
                      canEdit && date && updateWindow(window.chapter_id, 'opening', date)
                    }
                    disabled={!canEdit}
                    changed={canEdit && hasFieldChanged(window.chapter_id, 'opening')}

                  />
                </td>
                <td>
                  <DatePicker
                    utc
                    value={window.soft_deadline}
                    onChange={(date) =>
                      canEdit && date &&
                      updateWindow(window.chapter_id, 'soft_deadline', date)
                    }
                    disabled={!canEdit}
                    endOfDay
                    changed={canEdit && hasFieldChanged(window.chapter_id, 'soft_deadline')}

                  />
                </td>
                <td>
                  <DatePicker
                    utc
                    value={window.hard_deadline}
                    onChange={(date) =>
                      canEdit && date && updateWindow(window.chapter_id, 'hard_deadline', date)
                    }
                    disabled={!canEdit}
                    endOfDay
                    changed={canEdit && hasFieldChanged(window.chapter_id, 'hard_deadline')}

                  />
                </td>
                <td>
                  <DatePicker
                    utc
                    value={window.notification_date}
                    onChange={(date) =>
                      canEdit && date &&
                      updateWindow(window.chapter_id, 'notification_date', date)
                    }
                    disabled={!canEdit}
                    endOfDay
                    changed={canEdit && hasFieldChanged(window.chapter_id, 'notification_date')}

                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="application-window-actions">
        <PrismButton
          tone="secondary"
          display="outline"
          size="lg"
          disabled={!hasChanges}
          onPrismClick={initializeWindows}
        >
          Reset Changes
        </PrismButton>
        <div className="application-window-actions-status">
          {hasChanges && (
            <strong className="application-window-status-warning">You have unsaved changes</strong>
          )}
          {!hasEditableChapters && (
            <span className="application-window-status-info">
              <strong>Note:</strong> You have read-only access to these records.
            </span>
          )}
        </div>
        <PrismButton
          tone="primary"
          display="fill"
          size="lg"
          loading={submitting}
          disabled={submitting || !hasChanges || !hasEditableChapters}
          onPrismClick={() => submit()}
          title={!hasEditableChapters ? "You don't have permission to edit any chapters" : ""}
        >
          {submitting ? 'Saving...' : 'Save All Changes'}
        </PrismButton>
      </div>
    </form>
    </AdminPane>
  );
};

export default ApplicationWindow;